import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

// Stempel-format: "── Naam · DD-MM-YYYY HH:MM ──"
const STEMPEL_REGEX = /^── (.+?) · (\d{2}-\d{2}-\d{4} \d{2}:\d{2}) ──$/;

function nuStempel() {
  const nu = new Date();
  const dd = String(nu.getDate()).padStart(2, '0');
  const mm = String(nu.getMonth() + 1).padStart(2, '0');
  const yyyy = nu.getFullYear();
  const hh = String(nu.getHours()).padStart(2, '0');
  const min = String(nu.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
}

function parseEntries(text) {
  if (!text) return { legacy: '', entries: [] };
  const lines = text.split('\n');
  const entries = [];
  let huidige = null;
  const legacyLines = [];
  let preStempel = true;

  for (const line of lines) {
    const m = line.match(STEMPEL_REGEX);
    if (m) {
      if (huidige) entries.push(huidige);
      preStempel = false;
      huidige = { gebruiker: m[1], datum: m[2], inhoud: '' };
    } else if (huidige) {
      huidige.inhoud += (huidige.inhoud ? '\n' : '') + line;
    } else if (preStempel) {
      legacyLines.push(line);
    }
  }
  if (huidige) entries.push(huidige);

  entries.forEach((e) => { e.inhoud = e.inhoud.replace(/^\s+|\s+$/g, ''); });
  return { legacy: legacyLines.join('\n').trim(), entries };
}

function serializeEntries(legacy, entries) {
  const parts = [];
  if (legacy) parts.push(legacy);
  for (const e of entries) {
    parts.push(`── ${e.gebruiker} · ${e.datum} ──\n${e.inhoud}`);
  }
  return parts.join('\n\n');
}

const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

// Splitst "DD-MM-YYYY HH:MM" in een datum-key (voor groepering), een leesbare
// datum (voor de scheiding) en de tijd (bij het bericht).
function splitsStempel(datumStr) {
  const m = (datumStr || '').match(/^(\d{2})-(\d{2})-(\d{4}) (\d{2}:\d{2})$/);
  if (!m) return { key: datumStr || '', leesbaar: datumStr || '', tijd: '' };
  const [, dd, mm, yyyy, tijd] = m;
  const maandNaam = MAANDEN[parseInt(mm, 10) - 1] || mm;
  return {
    key: `${yyyy}-${mm}-${dd}`,
    leesbaar: `${parseInt(dd, 10)} ${maandNaam} ${yyyy}`,
    tijd,
  };
}

export default function OpmerkingenVeld({ initialValue, onSave }) {
  const { gebruiker } = useAuth();
  const naam = gebruiker?.naam || '';
  const [nieuweNotitie, setNieuweNotitie] = useState('');
  const [status, setStatus] = useState('idle');
  const [parsedData, setParsedData] = useState(() => parseEntries(initialValue || ''));
  const [verwijderTarget, setVerwijderTarget] = useState(null); // {type:'entry'|'legacy', idx?}
  const [ctxMenu, setCtxMenu] = useState(null); // {x, y, target:{type, idx?}}
  const [actieveDatum, setActieveDatum] = useState('');
  const [toonTopFade, setToonTopFade] = useState(false);
  const statusTimeoutRef = useRef(null);
  const textareaRef = useRef(null);
  const lijstRef = useRef(null);

  // Scroll naar de nieuwste opmerking (onderaan) bij laden en na toevoegen.
  useEffect(() => {
    const el = lijstRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [parsedData]);

  // Bepaal welke dag-groep bovenaan in beeld staat → vaste datum-kop.
  useEffect(() => {
    const el = lijstRef.current;
    if (!el) return;
    const bepaal = () => {
      const groepen = el.querySelectorAll('[data-datum]');
      const top = el.getBoundingClientRect().top;
      let huidige = '';
      for (const g of groepen) {
        // Laatste groep waarvan de bovenkant nog boven de zichtbare bovenrand ligt.
        if (g.getBoundingClientRect().top - top <= 8) huidige = g.getAttribute('data-datum');
        else break;
      }
      if (!huidige && groepen.length) huidige = groepen[0].getAttribute('data-datum');
      setActieveDatum(huidige);
      setToonTopFade(el.scrollTop > 6);
    };
    bepaal();
    el.addEventListener('scroll', bepaal);
    return () => el.removeEventListener('scroll', bepaal);
  }, [parsedData]);

  // Contextmenu sluiten bij klik-buiten, scroll of Escape.
  useEffect(() => {
    if (!ctxMenu) return;
    const sluit = () => setCtxMenu(null);
    const opToets = (e) => { if (e.key === 'Escape') setCtxMenu(null); };
    document.addEventListener('click', sluit);
    document.addEventListener('scroll', sluit, true);
    document.addEventListener('keydown', opToets);
    return () => {
      document.removeEventListener('click', sluit);
      document.removeEventListener('scroll', sluit, true);
      document.removeEventListener('keydown', opToets);
    };
  }, [ctxMenu]);

  // Auto-resize: laat de textarea meegroeien met de inhoud (max ~6 regels).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [nieuweNotitie]);

  useEffect(() => {
    setParsedData(parseEntries(initialValue || ''));
  }, [initialValue]);

  const saveNaarServer = async (nieuwParsed) => {
    setStatus('opslaan');
    try {
      const tekst = serializeEntries(nieuwParsed.legacy, nieuwParsed.entries);
      await onSave(tekst);
      setStatus('opgeslagen');
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => setStatus('idle'), 2500);
    } catch (e) {
      setStatus('fout');
    }
  };

  const voegToe = async () => {
    const txt = nieuweNotitie.trim();
    if (!txt) return;
    if (!naam) return;
    const nieuw = { gebruiker: naam, datum: nuStempel(), inhoud: txt };
    const nieuwParsed = {
      legacy: parsedData.legacy,
      entries: [...parsedData.entries, nieuw],
    };
    setParsedData(nieuwParsed);
    setNieuweNotitie('');
    await saveNaarServer(nieuwParsed);
  };

  const bevestigVerwijder = async () => {
    if (!verwijderTarget) return;
    let nieuwParsed;
    if (verwijderTarget.type === 'legacy') {
      nieuwParsed = { legacy: '', entries: parsedData.entries };
    } else {
      nieuwParsed = {
        legacy: parsedData.legacy,
        entries: parsedData.entries.filter((_, i) => i !== verwijderTarget.idx),
      };
    }
    setParsedData(nieuwParsed);
    setVerwijderTarget(null);
    await saveNaarServer(nieuwParsed);
  };

  const onTextareaKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      voegToe();
    }
  };

  // Chronologisch (oudste boven, nieuwste onder) — chatscherm-volgorde.
  const entriesChrono = parsedData.entries.map((e, i) => ({ ...e, _idx: i }));

  const verwijderKnopStijl = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text-muted, #999)', fontSize: 13, padding: 2, lineHeight: 1,
  };

  // Groepeer entries per dag voor de datumscheidingen.
  const groepen = [];
  let huidigeKey = null;
  for (const e of entriesChrono) {
    const s = splitsStempel(e.datum);
    if (s.key !== huidigeKey) {
      groepen.push({ datumLeesbaar: s.leesbaar, items: [] });
      huidigeKey = s.key;
    }
    groepen[groepen.length - 1].items.push({ ...e, tijd: s.tijd });
  }

  const heeftOpmerkingen = entriesChrono.length > 0 || !!parsedData.legacy;

  return (
    <div className={`opmerkingen-card${heeftOpmerkingen ? '' : ' is-leeg'}`}>
      {/* Vaste datum-kop bovenin — verandert mee met scrollen */}
      {heeftOpmerkingen && actieveDatum && (
        <div className="opmerkingen-datumkop"><span>{actieveDatum}</span></div>
      )}
      {/* Lege staat */}
      {!heeftOpmerkingen && (
        <div className="opmerkingen-leeg">Nog geen opmerkingen. Voeg de eerste toe.</div>
      )}
      {/* Chatweergave */}
      {heeftOpmerkingen && (
        <div ref={lijstRef} className={`opmerkingen-lijst${toonTopFade ? ' heeft-top-fade' : ''}`}>
          {parsedData.legacy && (
            <div
              className="opmerking-bericht"
              onContextMenu={(ev) => { ev.preventDefault(); setCtxMenu({ x: ev.clientX, y: ev.clientY, target: { type: 'legacy' } }); }}
            >
              <div className="opmerking-meta">
                <span style={{ fontWeight: 600, color: 'var(--text-soft)', fontStyle: 'italic' }}>Ongedateerd</span>
              </div>
              <div className="opmerking-tekst">{parsedData.legacy}</div>
            </div>
          )}
          {groepen.map((groep, gi) => (
            <div key={gi} className="opmerking-groep" data-datum={groep.datumLeesbaar}>
              {groep.datumLeesbaar !== actieveDatum && (
                <div className="opmerking-datum">
                  <span>{groep.datumLeesbaar}</span>
                </div>
              )}
              {groep.items.map((e) => (
                <div
                  key={`${e._idx}`}
                  className="opmerking-bericht"
                  onContextMenu={(ev) => { ev.preventDefault(); setCtxMenu({ x: ev.clientX, y: ev.clientY, target: { type: 'entry', idx: e._idx } }); }}
                >
                  <div className="opmerking-meta">
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>{e.gebruiker}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{e.tijd}</span>
                  </div>
                  <div className="opmerking-tekst">{e.inhoud}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Nieuwe-notitie input — zweeft onderaan; chat faded eronder weg */}
      <div className="opmerkingen-invoer">
        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: 8,
          background: '#ffffff', borderRadius: 20, padding: '4px 4px 4px 14px',
        }}>
          <textarea
            ref={textareaRef}
            className="opmerking-input"
            rows={1}
            value={nieuweNotitie}
            onChange={(e) => setNieuweNotitie(e.target.value)}
            onKeyDown={onTextareaKey}
            placeholder="Nieuwe notitie…"
            style={{
              flex: 1, fontFamily: 'inherit', fontSize: 14, lineHeight: 1.5,
              background: 'transparent', border: 'none', outline: 'none',
              padding: '7px 0', boxSizing: 'border-box', resize: 'none',
              maxHeight: 140, overflowY: 'auto',
            }}
          />
          <button
            type="button"
            onClick={voegToe}
            disabled={!nieuweNotitie.trim() || status === 'opslaan'}
            title="Toevoegen"
            style={{
              flexShrink: 0, width: 32, height: 32, borderRadius: '50%', border: 'none',
              background: status === 'opgeslagen'
                ? '#16a34a'
                : (nieuweNotitie.trim() || status === 'opslaan' ? 'var(--text)' : 'var(--text-muted)'),
              color: '#fff',
              cursor: (nieuweNotitie.trim() && status !== 'opslaan') ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background .2s',
            }}
          >
            {status === 'opslaan' ? (
              <span className="opmerking-spinner" />
            ) : status === 'opgeslagen' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            )}
          </button>
        </div>
        {status === 'fout' && (
          <div style={{ fontSize: 12, color: '#d70015', textAlign: 'right', marginTop: 6 }}>
            Niet opgeslagen — probeer opnieuw
          </div>
        )}
      </div>

      {/* Rechtermuisklik-contextmenu */}
      {ctxMenu && (
        <div
          style={{
            position: 'fixed', left: ctxMenu.x, top: ctxMenu.y, zIndex: 1100,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, boxShadow: 'var(--shadow-card)', padding: 4, minWidth: 150,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="acties-item acties-item-danger"
            onClick={() => { setVerwijderTarget(ctxMenu.target); setCtxMenu(null); }}
          >
            Opmerking verwijderen
          </button>
        </div>
      )}

      {/* Verwijder-confirmation modal */}
      {verwijderTarget && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.3)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: 20,
            maxWidth: 400, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 16 }}>Opmerking verwijderen?</h3>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 16px 0' }}>
              Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setVerwijderTarget(null)}>Annuleren</button>
              <button className="btn btn-danger" onClick={bevestigVerwijder}>Verwijderen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
